import { createSketchWallRotationSnapshot, applySketchWallRotationSnapshot } from '../sketch_walls/sketch_wall_selection_system.js';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import {
  drawingObjectsForCoreGroup,
  getActiveCoreGroups
} from '../objects/group_service.js?v=20260904c';
import { drawObstacleInteractionOverlay } from '../obstacles/obstacle_render2d.js?v=20260903c';
import * as SelectionService from './selection_service.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import {
  clearActiveTool,
  setActiveTool,
  syncToolbarState
} from '../interaction/tool_service.js?v=20260904f';
import { setInteractionFlag } from '../interaction/interaction_transient_state.js?v=20260904f';
import { rotateWorldPoint, snap } from '../utils/common_constants.js?v=20260706d';
import {
  applyTransformSnapshotRotation,
  transformSnapshotBounds
} from '../interaction/drawing_transform_controller.js?v=20260904f';
import {
  drawingObjectState,
  drawingObjectWorldCorners
} from '../objects/drawing_object_registry.js?v=20260904f';
import { rotateGroup2D as rotateCoreGroup2D } from '../2d/transforms/rotate.js?v=20260904f';
import {
  getPlanSvg,
  screenToWorld,
  setStatus,
  svgEl,
  worldToScreen
} from '../render/plan_renderer.js?v=20260904f';
import {
  clearObstacleSelection,
  ensureObstacleState,
  normalizeObstacle,
  obstacleBounds,
  obstacleById,
  resetObstacleDrawState,
  selectObstacle,
  setObstacleTool
} from '../obstacles/obstacles.js?v=20260903c';
import {
  deactivateSketchWallDrawTool,
  sketchWallDrawToolActive
} from '../sketch_walls/sketch_wall_interaction.js?v=20260903c';
import { S } from '../app/app_state.js?v=20260817c';
import { recordUndo } from '../services/history_service.js?v=20260903c';

const CORE_MULTI_SELECTION_FIELDS = Object.freeze(['obstacleIds']);

function multiSelectionFieldsForOwner(owner) {
  if (owner === 'core') return CORE_MULTI_SELECTION_FIELDS;
  const system = SystemRegistry.allSystems().find(s => s.id === owner);
  return Array.isArray(system?.multiSelectionFields) ? system.multiSelectionFields : [];
}

function allMultiSelectionFields() {
  return [
    ...CORE_MULTI_SELECTION_FIELDS,
    ...SystemRegistry.allSystems().flatMap(s => s.multiSelectionFields || [])
  ];
}


let multiSelectionRotatePointerMoveHandler = null;
let multiSelectionRotatePointerUpHandler = null;
let multiSelectionRotateContext = null;
let rotationAngleEditor = null;
let rotationAngleKeyHandler = null;
let suppressMultiSelectionRotateClick = false;

const CORE_GROUP_ROTATE_STROKE = '#16a34a';
const CORE_GROUP_ROTATE_FILL = '#15803d';

function emptyMultiSelection() {
    const obj = {};
    for (const field of CORE_MULTI_SELECTION_FIELDS) obj[field] = [];
    for (const system of SystemRegistry.allSystems()) {
      for (const field of system.multiSelectionFields || []) obj[field] = [];
    }
    return obj;
  }

function clearSelectionForActiveSystem() {
    clearObstacleSelection();
    clearMultiSelection();
    if (SystemRegistry.clearActiveSelection()) {
      return;
    }
    SystemRegistry.clearAllSelections();
  }

function hasGlobalSelection() {
    const selection = S.selection || {};
    if ((selection.items || []).length) return true;
    if (selection.dimensionId || selection.areaMeasurementId || selection.textAnnotationId) return true;
    if ((selection.obstacleIds || []).length || selection.obstacleId) return true;
    return false;
  }

function clearGlobalSelection() {
    const hadSelection = hasGlobalSelection();
    SystemRegistry.clearAllSelections();
    clearObstacleSelection();
    if (S.selection?.dimensionId) S.selection.dimensionId = null;
    if (S.selection?.areaMeasurementId) S.selection.areaMeasurementId = null;
    if (S.selection?.textAnnotationId) S.selection.textAnnotationId = null;
    clearMultiSelection();
    SystemRegistry.clearTransientStates({ currentView: S.project?.currentView || 'plan' });
    SelectionService.clear({ legacy: false });
    return hadSelection;
  }

function clearSelectionBeforeInsert() {
    return clearGlobalSelection();
  }

function canMoveElement(element) {
    if (!element || typeof element !== 'object') return true;
    return element.positionLocked !== true && element.isLocked !== true && element.locked !== true;
  }

function resetTransientInteractionState() {
    rotationAngleEditor?.finish(true);
    if (!S.ui || typeof S.ui !== 'object') S.ui = {};
    if (!S.drag || typeof S.drag !== 'object') S.drag = {};

    const cancelContext = { currentView: S.project?.currentView || 'plan' };

    SystemRegistry.cancelInteractions(cancelContext);
    SystemRegistry.clearTransientStates({ ...cancelContext, resetTools: true });

    resetObstacleDrawState();
    setObstacleTool(null);
    // Sketch walls track their own draw-tool flag (S.ui.sketchWallTool)
    // outside S.selection, so the generic selection-signature-based
    // auto-close in closeLevelsPanelIfRightSidebarIsTaken() can't see it —
    // deactivate it explicitly to keep the right sidebar single-panel rule
    // (see AGENTS.md).
    if (sketchWallDrawToolActive()) deactivateSketchWallDrawTool();

    S.drag.active = false;
    S.drag.type = null;
    S.drag.snapCandidate = null;
    S.drag.startSnapCandidate = null;
    S.drag.snapOptions = null;
    S.drag.crosslatBridgeCandidate = null;
    S.drag.crosslatBridgeCandidates = [];
    S.drag.tBridgeActive = false;
    S.drag.allowShortAfterBridge = false;
    S.drag.firstMovePending = false;
    S.drag.lastPreviewSnapDebugKey = null;

    clearGlobalSelection();
    clearActiveTool();
  }

function openLevelsPanelFromNeutralState() {
    resetTransientInteractionState();
    if (!S.ui || typeof S.ui !== 'object') S.ui = {};
    delete S.ui.levelsPanelSelectionSignature;
    S.ui.showLevelsPanel = true;
    setActiveTool({ owner: 'core', tool: 'levels' }, { toggle: false });
    setStatus('Ebenen geöffnet');
    AppRuntime.refresh(false);
  }

function toggleLevelsTool() {
    if (S.ui?.showLevelsPanel) {
      closeLevelsPanelForToolChange();
      setStatus('Ebenen deaktiviert');
      AppRuntime.refresh(false);
      return;
    }
    openLevelsPanelFromNeutralState();
  }

function closeLevelsPanelForToolChange(options = {}) {
    if (!S.ui || typeof S.ui !== 'object') S.ui = {};
    const wasOpen = Boolean(S.ui.showLevelsPanel);
    S.ui.showLevelsPanel = false;
    delete S.ui.levelsPanelSelectionSignature;
    S.ui.active3dLevelKey = null;
    if (!options.keepActiveTool && S.ui.activeTool?.owner === 'core' && S.ui.activeTool?.tool === 'levels') {
      S.ui.activeTool = null;
      syncToolbarState();
    }
    return wasOpen;
  }

function ensureMultiSelection() {
    return emptyMultiSelection();
  }

function resolvedMultiSelection() {
    const resolved = emptyMultiSelection();
    SystemRegistry.mergeLegacyMultiSelection(resolved, S.selection);
    if (Array.isArray(S.selection?.obstacleIds)) {
      resolved.obstacleIds.push(...S.selection.obstacleIds);
    } else if (S.selection?.obstacleId) {
      resolved.obstacleIds.push(S.selection.obstacleId);
    }
    for (const item of SelectionService.items()) {
      if (!item?.id) continue;
      if (item.owner === 'core' && item.kind === 'obstacle') {
        resolved.obstacleIds.push(item.id);
        continue;
      }
      const entry = SystemRegistry.resolveItemToMultiSelection(item);
      if (entry?.field) {
        if (!Array.isArray(resolved[entry.field])) resolved[entry.field] = [];
        if (entry.ref !== undefined) resolved[entry.field].push(entry.ref);
        else if (entry.id) resolved[entry.field].push(entry.id);
      }
    }
    for (const field of CORE_MULTI_SELECTION_FIELDS) {
      resolved[field] = [...new Set(resolved[field] || [])].filter(Boolean);
    }
    for (const system of SystemRegistry.allSystems()) {
      for (const field of system.multiSelectionFields || []) {
        resolved[field] = SystemRegistry.deduplicateMultiSelectionField(field, resolved[field] || []);
      }
    }
    return resolved;
  }

function clearMultiSelection() {
    syncLegacySelectionFromMultiSelection(emptyMultiSelection());
  }

function clearForeignMultiSelection(owner, selection = ensureMultiSelection()) {
    const keepFields = new Set(multiSelectionFieldsForOwner(owner));
    for (const field of allMultiSelectionFields()) {
      if (!keepFields.has(field)) selection[field] = [];
    }
    return selection;
  }

function clearOwnerMultiSelection(owner) {
    if (S.selection) {
      S.selection.items = (S.selection.items || []).filter(i => i.owner !== owner);
    }
    SystemRegistry.syncLegacyMultiSelectionForOwner(owner, emptyMultiSelection(), S.selection);
    SelectionService.syncFromLegacy();
  }

function countForeignMultiSelection(owner, selection = resolvedMultiSelection()) {
    const keepFields = new Set(multiSelectionFieldsForOwner(owner));
    return allMultiSelectionFields().reduce((count, field) => (
      keepFields.has(field)
        ? count
        : count + (Array.isArray(selection?.[field]) ? selection[field].length : 0)
    ), 0);
  }

function countForeignLegacySelection(owner) {
    return SelectionService.items().filter(item => item.owner !== owner).length;
  }

function hasMultiSelection() {
    return countMultiSelection() > 1;
  }

function multiSelectionContainsHit(hit) {
    const selection = resolvedMultiSelection();
    if (hit?.kind === 'obstacle') return selection.obstacleIds.includes(hit.obstacle?.id);
    return SystemRegistry.hitBelongsToSelection(hit, selection);
  }

function multiSelectionEntryFromHit(hit) {
    if (!hit || typeof hit !== 'object') return null;
    if (hit.kind === 'obstacle' || hit.obstacle?.id) {
      return hit.obstacle?.id ? { field: 'obstacleIds', id: hit.obstacle.id } : null;
    }
    return SystemRegistry.resolveHitToMultiSelection(hit);
  }

function countMultiSelection(selection = resolvedMultiSelection()) {
    return allMultiSelectionFields().reduce((count, field) => (
      count + (Array.isArray(selection?.[field]) ? selection[field].length : 0)
    ), 0);
  }

function syncLegacySelectionFromMultiSelection(selection = ensureMultiSelection()) {
    SystemRegistry.syncLegacyMultiSelection(selection, S.selection);
    S.selection.obstacleId = selection.obstacleIds?.at(-1) || null;
    S.selection.obstacleIds = [...(selection.obstacleIds || [])];
    SelectionService.syncFromLegacy();
  }

function syncSelectionFromItems(ownerSystemId) {
    const items = S.selection?.items || [];
    const selection = ensureMultiSelection();
    if (ownerSystemId) clearForeignMultiSelection(ownerSystemId, selection);
    for (const item of items) {
      const entry = SystemRegistry.resolveItemToMultiSelection(item);
      if (entry?.field && entry?.id) {
        if (!Array.isArray(selection[entry.field])) selection[entry.field] = [];
        const idStr = String(entry.id);
        if (!selection[entry.field].includes(idStr)) {
          selection[entry.field] = [...selection[entry.field], idStr];
        }
      }
    }
    syncLegacySelectionFromMultiSelection(selection);
  }

function toggleHitInMultiSelection(hit) {
    const itemEntry = SystemRegistry.resolveHitToSelectionItem(hit);

    if (itemEntry?.owner && itemEntry?.id) {
      const currentItems = S.selection?.items || [];
      let nextItems = currentItems.filter(i => i.owner === itemEntry.owner);
      const exists = nextItems.some(i => i.kind === itemEntry.kind && i.id === itemEntry.id);
      if (exists) {
        nextItems = nextItems.filter(i => !(i.kind === itemEntry.kind && i.id === itemEntry.id));
      } else {
        nextItems = [...nextItems, itemEntry];
      }
      if (S.selection) S.selection.items = nextItems;
      syncSelectionFromItems(itemEntry.owner);
      const count = (S.selection?.items || []).length;
      setStatus(count
        ? `${count} Objekt${count === 1 ? '' : 'e'} markiert`
        : 'Auswahl geleert');
      return true;
    }

    const entry = multiSelectionEntryFromHit(hit);
    if (!entry?.field || !entry.id) return false;
    const resolved = resolvedMultiSelection();
    const selection = ensureMultiSelection();
    for (const field of allMultiSelectionFields()) {
      selection[field] = [...new Set(resolved[field] || [])].filter(Boolean);
    }
    const id = String(entry.id);
    const ids = new Set(selection[entry.field] || []);
    const added = !ids.has(id);
    if (added) ids.add(id);
    else ids.delete(id);
    selection[entry.field] = Array.from(ids);
    syncLegacySelectionFromMultiSelection(selection);
    const count = countMultiSelection(selection);
    setStatus(count
      ? `${count} Objekt${count === 1 ? '' : 'e'} markiert`
      : 'Auswahl geleert');
    return true;
  }

function startPlanBoxSelection(evt) {
    evt.preventDefault();
    const rect = getPlanSvg().getBoundingClientRect();
    const world = screenToWorld(evt.clientX - rect.left, evt.clientY - rect.top);
    S.drag = {
      active: true,
      type: 'planBoxSelect',
      startMouse: world,
      currentMouse: world,
      startScreen: { x: evt.clientX, y: evt.clientY },
      currentScreen: { x: evt.clientX, y: evt.clientY }
    };
    setStatus('Auswahlrahmen ziehen');
  }

function finishPlanBoxSelection() {
    const drag = S.drag;
    const start = drag.startMouse;
    const current = drag.currentMouse || start;
    const worldRect = normalizedWorldRect(start, current);
    const screenA = drag.startScreen || worldToScreen(start.x, start.z);
    const screenB = drag.currentScreen || worldToScreen(current.x, current.z);
    const mode = planBoxSelectionMode(screenA, screenB);
    const screenDrag = Math.hypot(screenB.x - screenA.x, screenB.y - screenA.y);
    if (screenDrag < 6) return;

    const selection = collectObjectsInWorldRect(worldRect, mode);
    applyBoxSelection(selection);
    setInteractionFlag('suppressPlanClick', true);
    S.drag.active = false;
    S.drag.type = null;
    AppRuntime.requestAppRefresh(false, 'box-select-finish');
  }

function normalizedWorldRect(a, b) {
    return {
      minX: Math.min(a.x, b.x),
      minZ: Math.min(a.z, b.z),
      maxX: Math.max(a.x, b.x),
      maxZ: Math.max(a.z, b.z)
    };
  }

function planBoxSelectionMode(start, current) {
    return current.x < start.x ? 'crossing' : 'window';
  }

function collectObjectsInWorldRect(rect, mode = 'window') {
    const selection = SystemRegistry.collectBoxSelections(rect, mode);
    const obstacleIds = ensureObstacleState()
      .filter(obstacle => obstacleBoundsMatchesRect(obstacleBounds(obstacle), rect, mode))
      .map(obstacle => obstacle.id);
    selection.obstacleIds = [...new Set([...(selection.obstacleIds || []), ...obstacleIds])];
    return normalizeBoxSelection(selection);
  }

function obstacleBoundsMatchesRect(bounds, rect, mode = 'window') {
    if (!bounds || !rect) return false;
    if (mode === 'crossing') {
      return bounds.maxX >= rect.minX
        && bounds.minX <= rect.maxX
        && bounds.maxZ >= rect.minZ
        && bounds.minZ <= rect.maxZ;
    }
    return bounds.minX >= rect.minX
      && bounds.maxX <= rect.maxX
      && bounds.minZ >= rect.minZ
      && bounds.maxZ <= rect.maxZ;
  }

function normalizeBoxSelection(selection = {}) {
    const normalized = {};
    for (const field of allMultiSelectionFields()) {
      normalized[field] = [...new Set(selection[field] || [])].filter(Boolean);
    }
    for (const system of SystemRegistry.allSystems()) {
      for (const field of system.boxSelectionPassthroughFields || []) {
        normalized[field] = Array.isArray(selection[field]) ? selection[field] : [];
      }
    }
    return normalized;
  }

function applyBoxSelection(selection) {
    const fields = allMultiSelectionFields();
    const total = fields.reduce((sum, f) => sum + (selection[f]?.length || 0), 0);
    const boxSelection = { ...emptyMultiSelection() };
    for (const f of fields) boxSelection[f] = selection[f] || [];
    if (total > 1) {
      SystemRegistry.clearAllSelections();
      syncLegacySelectionFromMultiSelection(boxSelection);
      SystemRegistry.selectBoxSelection(selection, { multiple: true });
      setStatus(`${total} Objekte markiert`);
      return;
    }

    syncLegacySelectionFromMultiSelection(boxSelection);

    if (SystemRegistry.selectBoxSelection(selection, { multiple: false }) === true) {
      setStatus('1 Objekt markiert');
      return;
    }

    if (selection.obstacleIds?.length) {
      selectObstacle(selection.obstacleIds[0]);
    } else {
      SystemRegistry.clearAllSelections();
    }

    setStatus(total ? `${total} Objekt${total === 1 ? '' : 'e'} markiert` : 'Keine Objekte markiert');
  }

function startMultiSelectionDrag(evt) {
    evt.preventDefault();
    const rect = getPlanSvg().getBoundingClientRect();
    const world = screenToWorld(evt.clientX - rect.left, evt.clientY - rect.top);
    S.drag = {
      active: true,
      type: 'multiSelection',
      startMouse: world,
      hasMoved: false,
      ...buildMultiSelectionMoveSnapshot()
    };
    setStatus('Auswahl wird verschoben');
  }

function buildMultiSelectionMoveSnapshot(selection = resolvedMultiSelection()) {
    const normalizedSelection = normalizeBoxSelection(selection || {});
    const obstacleIds = [...new Set(selection?.obstacleIds || [])];
    return {
      selection: normalizedSelection,
      sketchWalls: createSketchWallRotationSnapshot(normalizedSelection.sketchWallIds || []),
      systemSnapshots: SystemRegistry.createMoveSnapshots({ selection: normalizedSelection, project: S.project }),
      lastDx: 0,
      lastDz: 0,
      startObstacles: obstacleIds
        .map(id => obstacleById(id))
        .filter(Boolean)
        .map(obstacle => ({
          id: obstacle.id,
          start: { ...obstacle.start },
          end: { ...obstacle.end }
        }))
    };
  }

function applyMultiSelectionMoveDelta(snapshot, dx, dz) {
    if (!snapshot) return 0;
    const snappedDx = S.project.snap ? snap(dx, S.project.gridSize) : dx;
    const snappedDz = S.project.snap ? snap(dz, S.project.gridSize) : dz;
    snapshot.lastDx = snappedDx;
    snapshot.lastDz = snappedDz;
    let count = 0;
    for (const systemSnapshot of snapshot.systemSnapshots || []) {
      count += SystemRegistry.applyMoveSnapshot(systemSnapshot, {
        selection: snapshot.selection,
        dx,
        dz,
        snappedDx,
        snappedDz,
        project: S.project
      });
    }
    for (const item of snapshot.startObstacles || []) {
      const obstacle = obstacleById(item.id);
      if (!obstacle) continue;
      obstacle.start = { x: item.start.x + snappedDx, z: item.start.z + snappedDz };
      obstacle.end = { x: item.end.x + snappedDx, z: item.end.z + snappedDz };
      normalizeObstacle(obstacle);
    }
    return count + (snapshot.startObstacles?.length || 0);
  }

function multiSelectionMoveSnapshotCount(snapshot) {
    if (!snapshot) return 0;
    return (snapshot.systemSnapshots || []).reduce((sum, systemSnapshot) => {
      const count = Number(systemSnapshot.count);
      return sum + (Number.isFinite(count) ? Math.max(0, count) : 0);
    }, 0) + (snapshot.startObstacles?.length || 0);
  }

function multiSelectionDrawingSnapshotItems(snapshot) {
    return (snapshot?.systemSnapshots || [])
      .flatMap(systemSnapshot => Array.isArray(systemSnapshot.items) ? systemSnapshot.items : []);
  }

function applyMultiSelectionDragPreview(snapshot, dx, dz) {
    if (!snapshot) return false;
    if (snapshot.startObstacles?.length) return false;
    const systemSnapshots = snapshot.systemSnapshots || [];
    if (!systemSnapshots.length) return false;
    const snappedDx = S.project.snap ? snap(dx, S.project.gridSize) : dx;
    const snappedDz = S.project.snap ? snap(dz, S.project.gridSize) : dz;
    for (const systemSnapshot of systemSnapshots) {
      const applied = SystemRegistry.applyMovePreview(systemSnapshot, {
        selection: snapshot.selection,
        dx,
        dz,
        snappedDx,
        snappedDz,
        project: S.project
      }) === true;
      if (!applied) {
        clearMultiSelectionDragPreview(snapshot);
        return false;
      }
    }
    snapshot.lastDx = snappedDx;
    snapshot.lastDz = snappedDz;
    return true;
  }

function clearMultiSelectionDragPreview(snapshot) {
    if (!snapshot) return;
    for (const systemSnapshot of snapshot.systemSnapshots || []) {
      SystemRegistry.clearMovePreview(systemSnapshot, {
        selection: snapshot.selection,
        project: S.project
      });
    }
  }

function mergeRotationBounds(a, b) {
    if (!a) return b || null;
    if (!b) return a || null;
    return {
      minX: Math.min(a.minX, b.minX),
      maxX: Math.max(a.maxX, b.maxX),
      minZ: Math.min(a.minZ, b.minZ),
      maxZ: Math.max(a.maxZ, b.maxZ)
    };
  }

function obstacleSnapshotBounds(item) {
    const points = [item?.start, item?.end].filter(point => (
      Number.isFinite(Number(point?.x)) && Number.isFinite(Number(point?.z))
    ));
    if (!points.length) return null;
    return points.reduce((bounds, point) => ({
      minX: Math.min(bounds.minX, Number(point.x)),
      maxX: Math.max(bounds.maxX, Number(point.x)),
      minZ: Math.min(bounds.minZ, Number(point.z)),
      maxZ: Math.max(bounds.maxZ, Number(point.z))
    }), {
      minX: Number(points[0].x),
      maxX: Number(points[0].x),
      minZ: Number(points[0].z),
      maxZ: Number(points[0].z)
    });
  }

function multiSelectionRotationBounds(snapshot) {
    if (!snapshot) return null;
    let bounds = (snapshot.sketchWalls?.points || []).reduce(mergeRotationPointBounds, null);
    for (const systemSnapshot of snapshot.systemSnapshots || []) {
      bounds = mergeRotationBounds(bounds, transformSnapshotBounds(systemSnapshot.items || []));
    }
    for (const obstacleSnapshot of snapshot.startObstacles || []) {
      bounds = mergeRotationBounds(bounds, obstacleSnapshotBounds(obstacleSnapshot));
    }
    return bounds;
  }

function rotationBoundsCorners(bounds) {
    if (!bounds) return [];
    return [
      { x: bounds.minX, z: bounds.minZ },
      { x: bounds.maxX, z: bounds.minZ },
      { x: bounds.maxX, z: bounds.maxZ },
      { x: bounds.minX, z: bounds.maxZ }
    ];
  }

function snapshotWorldPoints(snapshot) {
    const points = [...(snapshot?.sketchWalls?.points || [])];
    for (const systemSnapshot of snapshot?.systemSnapshots || []) {
      for (const item of systemSnapshot.items || []) {
        const corners = drawingObjectWorldCorners(item?.state);
        if (corners.length) {
          points.push(...corners);
          continue;
        }
        points.push(...rotationBoundsCorners(transformSnapshotBounds([item])));
      }
    }
    for (const obstacleSnapshot of snapshot?.startObstacles || []) {
      if (Number.isFinite(Number(obstacleSnapshot?.start?.x)) && Number.isFinite(Number(obstacleSnapshot?.start?.z))) {
        points.push({ x: Number(obstacleSnapshot.start.x), z: Number(obstacleSnapshot.start.z) });
      }
      if (Number.isFinite(Number(obstacleSnapshot?.end?.x)) && Number.isFinite(Number(obstacleSnapshot?.end?.z))) {
        points.push({ x: Number(obstacleSnapshot.end.x), z: Number(obstacleSnapshot.end.z) });
      }
    }
    return points;
  }

function multiSelectionRotationBoundsInWorldFrame(snapshot, rotation = 0) {
    const angle = Number(rotation) || 0;
    const origin = { x: 0, z: 0 };
    return snapshotWorldPoints(snapshot).reduce((bounds, point) => (
      mergeRotationPointBounds(
        bounds,
        Math.abs(angle) > 0.001 ? rotateWorldPoint(point, origin, -angle) : point
      )
    ), null);
  }

function multiSelectionOrientedRotationPivot(snapshot, rotation = 0) {
    const frameBounds = multiSelectionRotationBoundsInWorldFrame(snapshot, rotation);
    const frameCenter = boundsCenter(frameBounds);
    if (!frameCenter) return null;
    const angle = Number(rotation) || 0;
    return Math.abs(angle) > 0.001
      ? rotateWorldPoint(frameCenter, { x: 0, z: 0 }, angle)
      : frameCenter;
  }

function validRotationBounds(bounds) {
    return bounds && ['minX', 'maxX', 'minZ', 'maxZ'].every(key => Number.isFinite(Number(bounds[key])));
  }

function mergeRotationPointBounds(bounds, point) {
    if (!point || !Number.isFinite(Number(point.x)) || !Number.isFinite(Number(point.z))) return bounds || null;
    const x = Number(point.x);
    const z = Number(point.z);
    if (!bounds) return { minX: x, maxX: x, minZ: z, maxZ: z };
    return {
      minX: Math.min(bounds.minX, x),
      maxX: Math.max(bounds.maxX, x),
      minZ: Math.min(bounds.minZ, z),
      maxZ: Math.max(bounds.maxZ, z)
    };
  }

function transformSnapshotFrameBounds(snapshot, pivot, rotation = 0) {
    if (!pivot) return null;
    let frameBounds = null;
    let itemCount = 0;
    let localBoundsCount = 0;
    const angle = Number(rotation) || 0;
    for (const item of snapshot || []) {
      itemCount += 1;
      const localBounds = item?.state?.metadata?.localBounds;
      if (!validRotationBounds(localBounds)) continue;
      localBoundsCount += 1;
      const localCenter = boundsCenter(localBounds);
      if (!localCenter) continue;
      const frameCenter = Math.abs(angle) > 0.001
        ? rotateWorldPoint(localCenter, pivot, -angle)
        : localCenter;
      const halfWidth = (Number(localBounds.maxX) - Number(localBounds.minX)) / 2;
      const halfDepth = (Number(localBounds.maxZ) - Number(localBounds.minZ)) / 2;
      const frameLocalBounds = {
        minX: frameCenter.x - halfWidth,
        maxX: frameCenter.x + halfWidth,
        minZ: frameCenter.z - halfDepth,
        maxZ: frameCenter.z + halfDepth
      };
      for (const point of rotationBoundsCorners(frameLocalBounds)) {
        frameBounds = mergeRotationPointBounds(frameBounds, point);
      }
    }
    return itemCount > 0 && localBoundsCount === itemCount ? frameBounds : null;
  }

function boundsCenter(bounds) {
    return bounds
      ? { x: (bounds.minX + bounds.maxX) / 2, z: (bounds.minZ + bounds.maxZ) / 2 }
      : null;
  }

function normalizedRotationDelta(a, b) {
    let delta = ((Number(a) || 0) - (Number(b) || 0)) % 360;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    return delta;
  }

function multiSelectionReferenceRotation(snapshot) {
    const references = [];
    for (const systemSnapshot of snapshot?.systemSnapshots || []) {
      for (const item of systemSnapshot.items || []) {
        const rotation = Number(item?.state?.rotation);
        if (Number.isFinite(rotation)) references.push({
          key: `${item.state.systemType || systemSnapshot.systemId || ''}:${item.state.id || item.id || ''}`,
          rotation
        });
      }
    }
    // A mixed selection still has an orientation: use a stable object reference
    // instead of resetting to zero whenever its members have different angles.
    references.sort((a, b) => a.key.localeCompare(b.key));
    return references[0]?.rotation ?? snapshot?.sketchWalls?.rotation ?? 0;
  }

function multiSelectionCommonStoredPivot(snapshot) {
    const pivots = [];
    for (const systemSnapshot of snapshot?.systemSnapshots || []) {
      for (const item of systemSnapshot.items || []) {
        const pivot = item?.state?.groupRotationPivot;
        if (!Number.isFinite(Number(pivot?.x)) || !Number.isFinite(Number(pivot?.z))) continue;
        pivots.push({ x: Number(pivot.x), z: Number(pivot.z) });
      }
    }
    if (!pivots.length) return null;
    const base = pivots[0];
    return pivots.every(pivot => Math.hypot(pivot.x - base.x, pivot.z - base.z) < 1)
      ? base
      : null;
  }

function multiSelectionRotationPivot(snapshot, rotation = multiSelectionReferenceRotation(snapshot)) {
    const boundsPivot = multiSelectionOrientedRotationPivot(snapshot, rotation)
      || boundsCenter(multiSelectionRotationBounds(snapshot));
    return boundsPivot || multiSelectionCommonStoredPivot(snapshot);
  }

function multiSelectionRotationBoundsInFrame(snapshot, pivot, rotation = 0) {
    let frameBounds = null;
    for (const systemSnapshot of snapshot?.systemSnapshots || []) {
      frameBounds = mergeRotationBounds(
        frameBounds,
        transformSnapshotFrameBounds(systemSnapshot.items || [], pivot, rotation)
      );
    }
    if (frameBounds && !snapshot?.sketchWalls) return frameBounds;
    const worldBounds = multiSelectionRotationBounds(snapshot);
    if (!worldBounds || !pivot) return worldBounds;
    const angle = Number(rotation) || 0;
    if (Math.abs(angle) < 0.001) return worldBounds;
    return rotationBoundsCorners(worldBounds).reduce((bounds, point) => (
      mergeRotationPointBounds(bounds, rotateWorldPoint(point, pivot, -angle))
    ), null);
  }

function applyMultiSelectionRotation(snapshot, angleDeg = 90, options = {}) {
    if (!snapshot) return 0;
    const pivot = finitePoint(options.pivot) || multiSelectionRotationPivot(snapshot);
    if (!pivot) return 0;
    let count = applySketchWallRotationSnapshot(snapshot.sketchWalls, angleDeg, pivot);
    const drawingItems = multiSelectionDrawingSnapshotItems(snapshot);
    if (drawingItems.length) {
      count += applyTransformSnapshotRotation(drawingItems, angleDeg, {
        pivot,
        groupId: `multi-selection:${drawingItems.map(item => item.id).filter(Boolean).join('|')}`
      });
    }
    for (const item of snapshot.startObstacles || []) {
      const obstacle = obstacleById(item.id);
      if (!obstacle) continue;
      obstacle.start = rotateWorldPoint(item.start, pivot, angleDeg);
      obstacle.end = rotateWorldPoint(item.end, pivot, angleDeg);
      normalizeObstacle(obstacle);
      count += 1;
    }
    // Siehe Kommentar bei rotateActiveCoreGroup() -- dieselbe Falle mit dem
    // Default-Parameter (ensureMultiSelection() statt resolvedMultiSelection()).
    syncLegacySelectionFromMultiSelection(resolvedMultiSelection());
    return count;
  }

function multiSelectionEventWorld(event) {
    const svg = getPlanSvg();
    if (!svg || !event) return null;
    const rect = svg.getBoundingClientRect();
    return screenToWorld(Number(event.clientX) - rect.left, Number(event.clientY) - rect.top);
  }

function multiSelectionPointerAngle(pivot, world) {
    return Math.atan2(Number(world.z) - Number(pivot.z), Number(world.x) - Number(pivot.x)) * 180 / Math.PI;
  }

function finitePoint(value) {
    if (!Number.isFinite(Number(value?.x)) || !Number.isFinite(Number(value?.z))) return null;
    return { x: Number(value.x), z: Number(value.z) };
  }

function finiteBounds(value) {
    if (!validRotationBounds(value)) return null;
    return {
      minX: Number(value.minX),
      maxX: Number(value.maxX),
      minZ: Number(value.minZ),
      maxZ: Number(value.maxZ)
    };
  }

function rotationSnapshotForDrawingObjects(objects = []) {
    const items = (objects || [])
      .map(object => ({
        id: object.id,
        systemType: object.systemType,
        state: drawingObjectState(object)
      }))
      .filter(item => item.state);
    return items.length
      ? { selection: {}, systemSnapshots: [{ items, count: items.length }], startObstacles: [] }
      : null;
  }

function uniqueDrawingObjectsForCoreGroups(groups = []) {
    const byId = new Map();
    for (const group of groups || []) {
      for (const object of drawingObjectsForCoreGroup(group)) {
        if (!object || object.rotatable === false || byId.has(object.id)) continue;
        byId.set(object.id, object);
      }
    }
    return [...byId.values()];
  }

function rotatableDrawingObjectsForCoreGroup(group = {}) {
    return drawingObjectsForCoreGroup(group).filter(object => object?.rotatable !== false);
  }

function temporaryCoreGroupFromSelection(groups = [], objects = []) {
    const groupIds = (groups || []).map(group => group.groupId || group.id).filter(Boolean);
    return {
      id: `temporary-core-group:${groupIds.join('|')}`,
      groupId: `temporary-core-group:${groupIds.join('|')}`,
      key: `temporary-core-group:${groupIds.join('|')}`,
      label: `${groups.length} Gruppen`,
      displayName: `${groups.length} Gruppen`,
      systemId: 'core',
      systemKey: 'core',
      productLabel: 'Auswahl',
      objectIds: objects.map(object => object.id).filter(Boolean),
      active: true,
      temporarySelectionGroup: true,
      rawGroups: groups
  };
}

function rotationTargetFromCoreObjectGroups(groups = [], options = {}) {
    const normalizedGroups = (groups || []).filter(Boolean);
    if (!normalizedGroups.length) return null;
    const hasTemporarySelectionGroup = normalizedGroups.length > 1;
    const group = hasTemporarySelectionGroup
      ? temporaryCoreGroupFromSelection(normalizedGroups, [])
      : normalizedGroups[0];
    const objects = hasTemporarySelectionGroup
      ? uniqueDrawingObjectsForCoreGroups(normalizedGroups)
      : rotatableDrawingObjectsForCoreGroup(group);
    if (!objects.length) return null;
    if (hasTemporarySelectionGroup) {
      group.objectIds = objects.map(object => object.id).filter(Boolean);
    }
    const snapshot = rotationSnapshotForDrawingObjects(objects);
    if (!multiSelectionMoveSnapshotCount(snapshot)) return null;
    const count = objects.length;
    const snapshotBounds = multiSelectionRotationBounds(snapshot);
    const snapshotRotation = multiSelectionReferenceRotation(snapshot);
    const snapshotPivot = multiSelectionRotationPivot(snapshot, snapshotRotation)
      || boundsCenter(snapshotBounds);
    const rotation = !hasTemporarySelectionGroup && Number.isFinite(Number(group.rotationDeg))
      ? Number(group.rotationDeg)
      : snapshotRotation;
    const pivot = hasTemporarySelectionGroup
      ? snapshotPivot
      : finitePoint(group.rotationPivot || group.pivot) || snapshotPivot;
    const bounds = (pivot ? multiSelectionRotationBoundsInFrame(snapshot, pivot, rotation) : null)
      || finiteBounds(group.planBounds || group.rotationBounds || group.bounds || group.bounds2D)
      || multiSelectionRotationBounds(snapshot);
    if (!bounds) return null;
    const resolvedPivot = pivot || boundsCenter(bounds);
    if (!resolvedPivot) return null;
    return {
      group,
      objects,
      snapshot,
      count,
      bounds,
      pivot: resolvedPivot,
      rotation,
      temporarySelectionGroup: hasTemporarySelectionGroup,
      activeGroups: normalizedGroups,
      objectSelectionTarget: options.objectSelectionTarget === true
    };
  }

function activeCoreObjectRotationTarget() {
    const activeObjects = SystemRegistry.collectCoreObjects()
      .filter(object => object?.active === true && object.visible !== false);
    const groups = activeObjects.map(object => {
      const systemId = String(object.systemId || object.systemKey || '').trim();
      const objectId = String(object.objectId || object.id || '').trim();
      if (!systemId || !objectId) return null;
      const bounds = finiteBounds(object.bounds || object.planBounds || object.rotationBounds || object.bounds2D);
      return {
        id: objectId,
        groupId: objectId,
        key: `${systemId}:object:${objectId}`,
        label: object.label || object.name || object.displayName || objectId,
        displayName: object.displayName || object.label || object.name || objectId,
        systemId,
        systemKey: systemId,
        productLabel: object.productLabel || '',
        objectIds: [objectId],
        bounds,
        rotationDeg: Number.isFinite(Number(object.rotationDeg))
          ? Number(object.rotationDeg)
          : Number(object.raw?.rotation) || 0,
        rotationPivot: finitePoint(object.rotationPivot)
          || (bounds ? boundsCenter(bounds) : null),
        active: true,
        raw: object
      };
    }).filter(Boolean);
    const target = rotationTargetFromCoreObjectGroups(groups, { objectSelectionTarget: true });
    if (target) return target;
    const selection = resolvedMultiSelection();
    if (!selection.sketchWallIds?.length) return null;
    const snapshot = buildMultiSelectionMoveSnapshot(selection);
    if (!snapshot.sketchWalls) return null;
    const rotation = multiSelectionReferenceRotation(snapshot);
    const pivot = multiSelectionRotationPivot(snapshot, rotation);
    const bounds = multiSelectionRotationBoundsInFrame(snapshot, pivot, rotation);
    if (!pivot || !bounds) return null;
    return { snapshot, rotation, pivot, bounds, count: multiSelectionMoveSnapshotCount(snapshot),
      objects: [], group: { systemId: 'core_sketch_walls', id: 'sketch-wall-selection' } };
  }

function activeCoreGroupRotationTarget() {
    const activeGroups = getActiveCoreGroups();
    const hasTemporarySelectionGroup = activeGroups.length > 1;
    const singleGroup = activeGroups.length === 1 ? activeGroups[0] : null;
    const group = hasTemporarySelectionGroup
      ? temporaryCoreGroupFromSelection(activeGroups, [])
      : singleGroup;
    if (!group) return activeCoreObjectRotationTarget();
    if (hasTemporarySelectionGroup) {
      const missingDrawingObjects = activeGroups.some(activeGroup => (
        rotatableDrawingObjectsForCoreGroup(activeGroup).length <= 0
      ));
      if (missingDrawingObjects) return activeCoreObjectRotationTarget();
    }
    return rotationTargetFromCoreObjectGroups(activeGroups) || activeCoreObjectRotationTarget();
  }

function snappedCoreGroupDragAngle(target, startRotation, pointerDelta) {
    if (!target?.group || target.temporarySelectionGroup) return pointerDelta;
    const systemId = target.group.systemId || target.group.systemKey || '';
    const requestedRotation = (Number(startRotation) || 0) + (Number(pointerDelta) || 0);
    const snappedRotation = SystemRegistry.snapCoreGroupRotation(
      systemId,
      target.group,
      requestedRotation,
      { target }
    );
    return normalizedRotationDelta(snappedRotation, startRotation);
  }

function finalizeCoreGroupRotationTarget(target, context = {}) {
    if (!target) return false;
    const groups = target.temporarySelectionGroup
      ? (target.activeGroups || [])
      : [target.group];
    let finalized = false;
    for (const group of groups) {
      if (!group) continue;
      const systemId = group.systemId || group.systemKey || '';
      finalized = SystemRegistry.finalizeCoreGroupRotation(systemId, group, {
        target,
        objects: target.objects || [],
        ...context
      }) || finalized;
    }
    return finalized;
  }

function rotateActiveCoreGroup(target, angleDeg = 90, options = {}) {
    if (!target?.group) return false;
    const angle = Number(angleDeg) || 0;
    const objects = Array.isArray(target.objects) ? target.objects.filter(object => object?.rotatable !== false) : [];
    if (objects.length) {
      if (options.skipUndo !== true) {
        recordUndo(target.objectSelectionTarget
          ? (target.temporarySelectionGroup ? 'Objektauswahl drehen' : 'Objekt drehen')
          : (target.temporarySelectionGroup ? 'Gruppenauswahl drehen' : 'Gruppe drehen'));
      }
      const rotated = rotateCoreGroup2D({
        group: {
          ...target.group,
          objects,
          primaryObject: objects[0],
          systemType: target.group.systemId || target.group.systemKey || objects[0]?.systemType || ''
        },
        angleDeg: angle,
        pivot: target.pivot,
        exactTransform: true,
        forcePivot: true,
        source: 'drawing-view-group-rotate-button'
      });
      if (!rotated) return false;
      // resolvedMultiSelection() statt der leeren Default-Vorbelegung, sonst
      // wird S.selection.groupId bei jedem Live-Dreh-Zwischenschritt auf null
      // zurueckgesetzt und ein nachfolgender Dreh-Versuch erwischt einen
      // veralteten Griff (Akkumulations-Bug).
      syncLegacySelectionFromMultiSelection(resolvedMultiSelection());
      const targetLabel = target.objectSelectionTarget
        ? (target.temporarySelectionGroup ? 'Objektauswahl' : 'Objekt')
        : (target.temporarySelectionGroup ? 'Gruppenauswahl' : 'Gruppe');
      if (options.live === true) {
        const displayRotation = Number.isFinite(Number(options.displayRotationDeg))
          ? Number(options.displayRotationDeg)
          : Number.isFinite(Number(options.totalAngleDeg))
            ? Number(options.totalAngleDeg)
            : angle;
        setStatus(`${targetLabel} wird frei gedreht · ${Math.round(displayRotation)}°`);
      } else {
        finalizeCoreGroupRotationTarget(target, { source: options.source || 'core-group-rotate' });
        setStatus(target.objectSelectionTarget
          ? (target.temporarySelectionGroup ? 'Objektauswahl um 90° gedreht' : 'Objekt um 90° gedreht')
          : (target.temporarySelectionGroup ? 'Gruppenauswahl um 90° gedreht' : 'Gruppe um 90° gedreht'));
        AppRuntime.requestAppRefresh(false, 'core-group-rotate');
      }
      return true;
    }
    if (!target.count) return false;
    if (options.skipUndo !== true) recordUndo('Gruppe drehen');
    const fallbackAngle = options.live === true && Number.isFinite(Number(options.totalAngleDeg))
      ? Number(options.totalAngleDeg)
      : angle;
    const rotated = applyMultiSelectionRotation(target.snapshot, fallbackAngle, { pivot: target.pivot });
    if (options.live === true) {
      const displayRotation = Number.isFinite(Number(options.displayRotationDeg))
        ? Number(options.displayRotationDeg)
        : Number.isFinite(Number(options.totalAngleDeg))
          ? Number(options.totalAngleDeg)
          : angle;
      setStatus(`${rotated} Objekte werden frei gedreht · ${Math.round(displayRotation)}°`);
      AppRuntime.requestPlanRender('core-group-rotate');
    } else {
      finalizeCoreGroupRotationTarget(target, { source: options.source || 'core-group-rotate' });
      setStatus(`${rotated} Objekte um Gruppenmittelpunkt gedreht`);
      AppRuntime.requestAppRefresh(false, 'core-group-rotate');
    }
    return true;
  }

function beginCoreGroupFreeRotate() {
    if (S.drag?.type !== 'coreGroupRotatePending') return false;
    recordUndo('Gruppe frei drehen');
    S.drag.type = 'coreGroupRotate';
    S.drag.lastAngle = 0;
    S.drag.freeRotateStarted = true;
    suppressMultiSelectionRotateClick = true;
    setStatus('Gruppe wird frei gedreht');
    AppRuntime.requestPlanRender('core-group-rotate-start');
    return true;
  }

function updateCoreGroupRotateDrag(event) {
    if (!event || !['coreGroupRotatePending', 'coreGroupRotate'].includes(S.drag?.type)) return false;
    const world = multiSelectionEventWorld(event);
    const drag = S.drag;
    if (!world || !drag.pivot || !drag.target) return false;
    if (drag.type === 'coreGroupRotatePending') {
      const dx = Number(event.clientX) - Number(drag.startScreen?.x);
      const dy = Number(event.clientY) - Number(drag.startScreen?.y);
      if (Math.hypot(dx, dy) < 4) return true;
      if (!beginCoreGroupFreeRotate()) return true;
    }
    const pointerDelta = normalizedRotationDelta(
      multiSelectionPointerAngle(drag.pivot, world),
      drag.startPointerAngle
    );
    const angle = snappedCoreGroupDragAngle(drag.target, drag.startRotation, pointerDelta);
    const delta = angle - Number(drag.lastAngle || 0);
    if (Math.abs(delta) > 0.001) {
      rotateActiveCoreGroup(drag.target, delta, {
        skipUndo: true,
        live: true,
        totalAngleDeg: angle,
        displayRotationDeg: (Number(drag.startRotation) || 0) + angle,
        rotationSessionId: drag.rotationSessionId
      });
      drag.lastAngle = angle;
    }
    AppRuntime.requestPlanRender('core-group-rotate');
    return true;
  }

function finishCoreGroupRotateDrag({ focusLoss = false } = {}) {
    if (!['coreGroupRotatePending', 'coreGroupRotate'].includes(S.drag?.type)) return false;
    const wasFreeRotate = S.drag.type === 'coreGroupRotate';
    const target = S.drag.target;
    clearMultiSelectionRotatePointerListeners();
    if (wasFreeRotate) {
      finalizeCoreGroupRotationTarget(target, { source: 'core-group-free-rotate-finish' });
      const targetLabel = target?.objectSelectionTarget
        ? (target.temporarySelectionGroup ? 'Objektauswahl' : 'Objekt')
        : (target?.temporarySelectionGroup ? 'Gruppenauswahl' : 'Gruppe');
      setStatus(`${targetLabel} frei gedreht`);
      suppressNextMultiSelectionRotateClick();
      AppRuntime.requestAppRefresh(false, 'core-group-rotate-finish');
    } else if (target && !focusLoss) {
      rotateActiveCoreGroup(target, 90);
      suppressNextMultiSelectionRotateClick();
    }
    if (focusLoss && !wasFreeRotate) suppressNextMultiSelectionRotateClick();
    S.drag = { active: false, type: null };
    return true;
  }

function startCoreGroupRotatePress(event, target, handleWorld = null) {
    if (!target?.pivot) return false;
    const world = multiSelectionEventWorld(event);
    if (!world) return false;
    const startHandleWorld = finitePoint(handleWorld) || world;
    event.preventDefault?.();
    event.stopPropagation?.();
    suppressMultiSelectionRotateClick = false;
    S.drag = {
      active: true,
      type: 'coreGroupRotatePending',
      target,
      pivot: target.pivot,
      handleStartWorld: startHandleWorld,
      startRotation: Number(target.rotation) || 0,
      startPointerAngle: multiSelectionPointerAngle(target.pivot, world),
      startScreen: { x: event.clientX, y: event.clientY },
      rotationSessionId: `core-group-rotate-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      lastAngle: 0
    };
    bindMultiSelectionRotatePointerListeners(event.pointerId);
    return true;
  }

function coreRotationAngleLabel(rotation) {
    const normalized = (((Number(rotation) || 0) % 360) + 360) % 360;
    return `${Math.round(normalized * 10) / 10 % 360}°`;
  }

// The editor owns only UI state. Rotation still uses the existing Core gesture
// and owner adapters, including their single Undo and completion boundary.
function openCoreRotationAngleEditor(firstDigit = null) {
    if (rotationAngleEditor || !currentMultiSelectionRotateContext()) return false;
    const drag = S.drag;
    const context = multiSelectionRotateContext;
    const label = getPlanSvg()?.querySelector('.core-rotation-angle');
    const rect = label?.getBoundingClientRect();
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.className = 'core-rotation-angle-input';
    input.setAttribute('aria-label', 'Drehwinkel in Grad');
    input.value = firstDigit ?? coreRotationAngleLabel(drag.startRotation + (drag.lastAngle || 0)).slice(0, -1);
    Object.assign(input.style, { position: 'fixed', zIndex: '10000',
      left: `${rect?.left ?? drag.startScreen?.x ?? 0}px`,
      top: `${(rect?.top ?? drag.startScreen?.y ?? 0) - 5}px`, width: '86px' });
    drag.angleEditing = true;
    const editor = { input, finish: null };
    rotationAngleEditor = editor;
    const finish = (cancel = false, focusLoss = false) => {
      if (rotationAngleEditor !== editor) return;
      const validContext = currentMultiSelectionRotateContext(context);
      const text = input.value.trim().replace(',', '.');
      const value = text ? Number(text) : NaN;
      const cancelled = cancel || (focusLoss && (!Number.isFinite(value) || value < 0 || value > 360));
      if (!cancelled && validContext && text && (!Number.isFinite(value) || value < 0 || value > 360)) {
        input.setCustomValidity('Bitte einen Winkel zwischen 0 und 360 eingeben.');
        input.reportValidity();
        input.focus();
        return;
      }
      rotationAngleEditor = null;
      window.removeEventListener('pointerdown', outside, true);
      input.remove();
      drag.angleEditing = false;
      if (!validContext) {
        if (multiSelectionRotateContext === context) clearMultiSelectionRotatePointerListeners();
        return;
      }
      // Escape or an empty field restores the orientation before this gesture.
      const angle = cancelled || !text ? 0 : value % 360 - drag.startRotation;
      const delta = angle - (drag.lastAngle || 0);
      if (Math.abs(delta) > 1e-9) {
        if (!drag.freeRotateStarted) {
          if (drag.type === 'coreGroupRotatePending') beginCoreGroupFreeRotate();
          else beginMultiSelectionFreeRotate();
        }
        if (drag.target) rotateActiveCoreGroup(drag.target, delta, {
          skipUndo: true, live: true, totalAngleDeg: angle,
          displayRotationDeg: drag.startRotation + angle
        });
        else applyMultiSelectionRotation(drag.snapshot, angle, { pivot: drag.pivot });
        drag.lastAngle = angle;
      }
      finishMultiSelectionRotateDrag({ focusLoss: true });
      AppRuntime.requestPlanRender('rotation-angle-input-finish');
    };
    function outside(event) {
      if (event.target === input) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setInteractionFlag('suppressPlanClick', true);
      finish();
    }
    editor.finish = finish;
    input.addEventListener('keydown', event => {
      event.stopPropagation();
      if (event.key === 'Enter' || event.key === 'Escape') {
        event.preventDefault();
        finish(event.key === 'Escape');
      }
    });
    input.addEventListener('input', () => input.setCustomValidity(''));
    input.addEventListener('blur', () => finish());
    input.addEventListener('pointerdown', event => event.stopPropagation());
    input.addEventListener('mousedown', event => event.stopPropagation());
    document.body.append(input);
    window.addEventListener('pointerdown', outside, true);
    input.focus();
    if (firstDigit === null) input.select();
    else input.setSelectionRange(input.value.length, input.value.length);
    AppRuntime.requestPlanRender('rotation-angle-input-start');
    return true;
  }

function bindCoreRotationAngleLabel(label, start) {
    label.style.cursor = 'text';
    label.setAttribute('pointer-events', 'all');
    label.addEventListener('mousedown', event => event.stopPropagation());
    label.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); });
    label.addEventListener('pointerdown', event => {
      event.preventDefault();
      event.stopPropagation();
      if (!currentMultiSelectionRotateContext()) start(event);
      openCoreRotationAngleEditor();
    });
    if (rotationAngleEditor) {
      if (!currentMultiSelectionRotateContext()) rotationAngleEditor.finish(true);
      else label.setAttribute('visibility', 'hidden');
    }
  }

function drawActiveCoreGroupRotateHandle() {
    const drag = ['coreGroupRotatePending', 'coreGroupRotate'].includes(S.drag?.type) ? S.drag : null;
    const target = drag?.target || activeCoreGroupRotationTarget();
    if (!target) return false;
    const svg = getPlanSvg();
    if (!svg) return false;
    const handleLocal = {
      x: (target.bounds.minX + target.bounds.maxX) / 2,
      z: target.bounds.minZ - 260
    };
    const dragHandleWorld = drag?.handleStartWorld && drag?.pivot
      ? rotateWorldPoint(drag.handleStartWorld, drag.pivot, Number(drag.lastAngle) || 0)
      : null;
    const handleWorld = dragHandleWorld || (Math.abs(Number(target.rotation) || 0) > 0.001
      ? rotateWorldPoint(handleLocal, target.pivot, target.rotation)
      : handleLocal);
    const p = worldToScreen(handleWorld.x, handleWorld.z);
    const btn = svgEl('g', {
      class: 'core-group-rotate-handle',
      style: 'cursor:pointer',
      'data-system-id': 'core',
      'data-core-group-rotate': 'true',
      'data-core-group-system-id': target.group.systemId || target.group.systemKey || '',
      'data-core-group-id': target.group.groupId || target.group.id || ''
    }, svg);
    svgEl('circle', {
      cx: p.x,
      cy: p.y,
      r: 15,
      fill: '#ffffff',
      stroke: CORE_GROUP_ROTATE_STROKE,
      'stroke-width': 2
    }, btn);
    svgEl('text', {
      x: p.x,
      y: p.y + 4,
      'text-anchor': 'middle',
      'font-size': 15,
      'font-weight': 700,
      fill: CORE_GROUP_ROTATE_FILL,
      'pointer-events': 'none'
    }, btn).textContent = '↻';
    const displayedRotation = drag
      ? (Number(drag.startRotation) || 0) + (Number(drag.lastAngle) || 0)
      : Number(target.rotation) || 0;
    const angleLabel = svgEl('text', {
      class: 'core-rotation-angle',
      x: p.x + 23,
      y: p.y + 4,
      'font-size': 12,
      'font-weight': 700,
      fill: CORE_GROUP_ROTATE_FILL,
      'pointer-events': 'none'
    }, svg);
    angleLabel.textContent = coreRotationAngleLabel(displayedRotation);
    bindCoreRotationAngleLabel(angleLabel, event => startCoreGroupRotatePress(event, target, handleWorld));
    btn.addEventListener('pointerdown', event => {
      startCoreGroupRotatePress(event, target, handleWorld);
    });
    btn.addEventListener('mousedown', event => event.stopPropagation?.());
    btn.addEventListener('click', event => {
      event.preventDefault?.();
      event.stopPropagation?.();
      if (suppressMultiSelectionRotateClick) return;
      rotateActiveCoreGroup(target, 90);
    });
    return true;
  }

function clearMultiSelectionRotatePointerListeners() {
    if (rotationAngleKeyHandler) {
      window.removeEventListener('keydown', rotationAngleKeyHandler, true);
      rotationAngleKeyHandler = null;
    }
    if (multiSelectionRotatePointerMoveHandler) {
      window.removeEventListener('pointermove', multiSelectionRotatePointerMoveHandler);
      multiSelectionRotatePointerMoveHandler = null;
    }
    if (multiSelectionRotatePointerUpHandler) {
      window.removeEventListener('pointerup', multiSelectionRotatePointerUpHandler);
      window.removeEventListener('pointercancel', multiSelectionRotatePointerUpHandler);
      multiSelectionRotatePointerUpHandler = null;
    }
    multiSelectionRotateContext = null;
  }

function currentMultiSelectionRotateContext(context = multiSelectionRotateContext) {
    return Boolean(context
      && context === multiSelectionRotateContext
      && context.project === S.project
      && context.sheetId === S.drawingSheets?.activeSheetId
      && context.drag === S.drag
      && S.drag?.active === true
      && S.project?.currentView === 'plan');
  }

function suppressNextMultiSelectionRotateClick() {
    suppressMultiSelectionRotateClick = true;
    setTimeout(() => {
      suppressMultiSelectionRotateClick = false;
    }, 350);
  }

function beginMultiSelectionFreeRotate() {
    if (S.drag?.type !== 'multiSelectionRotatePending') return false;
    recordUndo('Auswahl frei drehen');
    S.drag.type = 'multiSelectionRotate';
    S.drag.freeRotateStarted = true;
    suppressMultiSelectionRotateClick = true;
    setStatus('Auswahl wird frei gedreht');
    AppRuntime.requestPlanRender('multi-selection-rotate-start');
    return true;
  }

function updateMultiSelectionRotateDrag(event) {
    if (['coreGroupRotatePending', 'coreGroupRotate'].includes(S.drag?.type)) {
      return updateCoreGroupRotateDrag(event);
    }
    if (!event || !['multiSelectionRotatePending', 'multiSelectionRotate'].includes(S.drag?.type)) return false;
    const world = multiSelectionEventWorld(event);
    const drag = S.drag;
    if (!world || !drag.pivot) return false;
    if (drag.type === 'multiSelectionRotatePending') {
      const dx = Number(event.clientX) - Number(drag.startScreen?.x);
      const dy = Number(event.clientY) - Number(drag.startScreen?.y);
      if (Math.hypot(dx, dy) < 4) return true;
      if (!beginMultiSelectionFreeRotate()) return true;
    }
    const angle = multiSelectionPointerAngle(drag.pivot, world) - Number(drag.startPointerAngle || 0);
    const rotated = applyMultiSelectionRotation(drag.snapshot, angle, { pivot: drag.pivot });
    drag.lastAngle = angle;
    setStatus(`${rotated} Objekte werden frei gedreht · ${coreRotationAngleLabel((Number(drag.startRotation) || 0) + angle)}`);
    AppRuntime.requestPlanRender('multi-selection-rotate');
    return true;
  }

function finishMultiSelectionRotateDrag(options = {}) {
    if (['coreGroupRotatePending', 'coreGroupRotate'].includes(S.drag?.type)) {
      return finishCoreGroupRotateDrag(options);
    }
    if (!['multiSelectionRotatePending', 'multiSelectionRotate'].includes(S.drag?.type)) return false;
    const wasFreeRotate = S.drag.type === 'multiSelectionRotate';
    const count = multiSelectionMoveSnapshotCount(S.drag.snapshot);
    clearMultiSelectionRotatePointerListeners();
    if (wasFreeRotate) {
      // Snapshot rotation needs the same owner completion boundary as a
      // single core-group gesture, including systems with transient previews.
      for (const snapshot of S.drag.snapshot?.systemSnapshots || []) {
        const systemId = snapshot.systemId;
        const objects = (snapshot.items || []).map(item => item.state).filter(Boolean);
        SystemRegistry.finalizeCoreGroupRotation(systemId, { systemId, objects }, {
          objects, source: 'multi-selection-free-rotate-finish'
        });
      }
      setStatus(`${count} Objekte frei gedreht`);
      suppressNextMultiSelectionRotateClick();
      AppRuntime.requestAppRefresh(false, 'multi-selection-rotate-finish');
    }
    if (options.focusLoss && !wasFreeRotate) suppressNextMultiSelectionRotateClick();
    S.drag = { active: false, type: null };
    return true;
  }

function finishPlanRotationOnFocusLoss() {
    if (rotationAngleEditor) {
      rotationAngleEditor.finish(false, true);
      return true;
    }
    if (!currentMultiSelectionRotateContext()) {
      clearMultiSelectionRotatePointerListeners();
      return false;
    }
    return finishMultiSelectionRotateDrag({ focusLoss: true });
  }

function bindMultiSelectionRotatePointerListeners(pointerId = null) {
    clearMultiSelectionRotatePointerListeners();
    multiSelectionRotateContext = {
      project: S.project,
      sheetId: S.drawingSheets?.activeSheetId,
      drag: S.drag
    };
    const context = multiSelectionRotateContext;
    const eventContextIsCurrent = () => {
      if (currentMultiSelectionRotateContext(context)) return true;
      if (multiSelectionRotateContext === context) clearMultiSelectionRotatePointerListeners();
      return false;
    };
    const trackedPointerId = Number.isFinite(Number(pointerId)) ? Number(pointerId) : null;
    const eventBelongsToPress = event => (
      trackedPointerId === null
      || !Number.isFinite(Number(event?.pointerId))
      || Number(event.pointerId) === trackedPointerId
    );
    multiSelectionRotatePointerMoveHandler = event => {
      if (!eventContextIsCurrent()) return;
      if (!eventBelongsToPress(event)) return;
      event.preventDefault?.();
      if (!S.drag.angleEditing) updateMultiSelectionRotateDrag(event);
    };
    multiSelectionRotatePointerUpHandler = event => {
      if (!eventContextIsCurrent()) return;
      if (!eventBelongsToPress(event)) return;
      if (!S.drag.angleEditing) finishMultiSelectionRotateDrag();
    };
    rotationAngleKeyHandler = event => {
      if (!eventContextIsCurrent() || S.drag.angleEditing || event.ctrlKey || event.metaKey || event.altKey) return;
      if (!/^[0-9]$/.test(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openCoreRotationAngleEditor(event.key);
    };
    window.addEventListener('keydown', rotationAngleKeyHandler, true);
    window.addEventListener('pointermove', multiSelectionRotatePointerMoveHandler, { passive: false });
    window.addEventListener('pointerup', multiSelectionRotatePointerUpHandler);
    window.addEventListener('pointercancel', multiSelectionRotatePointerUpHandler);
  }

function startMultiSelectionRotatePress(event, handleWorld = null) {
    const snapshot = buildMultiSelectionMoveSnapshot(resolvedMultiSelection());
    const count = multiSelectionMoveSnapshotCount(snapshot);
    if (count <= 1) return false;
    const rotation = multiSelectionReferenceRotation(snapshot);
    const pivot = multiSelectionRotationPivot(snapshot, rotation);
    const world = multiSelectionEventWorld(event);
    if (!pivot || !world) return false;
    const startHandleWorld = finitePoint(handleWorld) || world;
    event.preventDefault?.();
    event.stopPropagation?.();
    suppressMultiSelectionRotateClick = false;
    S.drag = {
      active: true,
      type: 'multiSelectionRotatePending',
      snapshot,
      pivot,
      handleStartWorld: startHandleWorld,
      startRotation: rotation,
      lastAngle: 0,
      startPointerAngle: multiSelectionPointerAngle(pivot, world),
      startScreen: { x: event.clientX, y: event.clientY }
    };
    bindMultiSelectionRotatePointerListeners(event.pointerId);
    return true;
  }

function drawMultiSelectionRotateHandle() {
    const drag = ['multiSelectionRotatePending', 'multiSelectionRotate'].includes(S.drag?.type) ? S.drag : null;
    const snapshot = buildMultiSelectionMoveSnapshot(resolvedMultiSelection());
    const count = multiSelectionMoveSnapshotCount(snapshot);
    if (count <= 1) return false;
    const rotation = multiSelectionReferenceRotation(snapshot);
    const pivot = multiSelectionRotationPivot(snapshot, rotation);
    const bounds = multiSelectionRotationBoundsInFrame(snapshot, pivot, rotation);
    if (!bounds) return false;
    const svg = getPlanSvg();
    if (!svg) return false;
    const handleLocal = {
      x: (bounds.minX + bounds.maxX) / 2,
      z: bounds.minZ - 260
    };
    const dragHandleWorld = drag?.handleStartWorld && drag?.pivot
      ? rotateWorldPoint(drag.handleStartWorld, drag.pivot, Number(drag.lastAngle) || 0)
      : null;
    const handleWorld = dragHandleWorld || (pivot && Math.abs(Number(rotation) || 0) > 0.001
      ? rotateWorldPoint(handleLocal, pivot, rotation)
      : handleLocal);
    const p = worldToScreen(handleWorld.x, handleWorld.z);
    const btn = svgEl('g', {
      class: 'core-multi-selection-rotate-handle',
      style: 'cursor:pointer',
      'data-system-id': 'core',
      'data-core-multi-selection-rotate': 'true'
    }, svg);
    svgEl('circle', {
      cx: p.x,
      cy: p.y,
      r: 15,
      fill: '#ffffff',
      stroke: CORE_GROUP_ROTATE_STROKE,
      'stroke-width': 2
    }, btn);
    svgEl('text', {
      x: p.x,
      y: p.y + 4,
      'text-anchor': 'middle',
      'font-size': 15,
      'font-weight': 700,
      fill: CORE_GROUP_ROTATE_FILL,
      'pointer-events': 'none'
    }, btn).textContent = '↻';
    const displayedRotation = drag
      ? (Number(drag.startRotation) || 0) + (Number(drag.lastAngle) || 0)
      : Number(rotation) || 0;
    const angleLabel = svgEl('text', {
      class: 'core-rotation-angle',
      x: p.x + 23,
      y: p.y + 4,
      'font-size': 12,
      'font-weight': 700,
      fill: CORE_GROUP_ROTATE_FILL,
      'pointer-events': 'none'
    }, svg);
    angleLabel.textContent = coreRotationAngleLabel(displayedRotation);
    bindCoreRotationAngleLabel(angleLabel, event => startMultiSelectionRotatePress(event, handleWorld));
    btn.addEventListener('pointerdown', event => {
      startMultiSelectionRotatePress(event, handleWorld);
    });
    btn.addEventListener('mousedown', event => event.stopPropagation?.());
    btn.addEventListener('click', event => {
      event.preventDefault?.();
      event.stopPropagation?.();
      if (suppressMultiSelectionRotateClick) return;
      const rotateSnapshot = buildMultiSelectionMoveSnapshot(resolvedMultiSelection());
      const rotateCount = multiSelectionMoveSnapshotCount(rotateSnapshot);
      if (rotateCount <= 1) return;
      recordUndo('Auswahl drehen');
      const rotated = applyMultiSelectionRotation(rotateSnapshot, 90);
      if (!rotated) return;
      setStatus(`${rotated} Objekte um Gruppenmittelpunkt gedreht`);
      AppRuntime.requestAppRefresh(false, 'multi-selection-rotate-handle');
    });
    return true;
  }

function syncSelectionAfterMultiMove(snapshot, targetSelection = ensureMultiSelection()) {
    if (!snapshot || !targetSelection) return 0;
    const fields = allMultiSelectionFields();
    const merged = Object.fromEntries(fields.map(f => [f, []]));
    for (const systemSnapshot of snapshot.systemSnapshots || []) {
      const selection = SystemRegistry.syncMoveSelection(systemSnapshot, {
        targetSelection,
        selection: snapshot.selection,
        dx: snapshot.lastDx || 0,
        dz: snapshot.lastDz || 0,
        project: S.project
      });
      if (!selection) continue;
      for (const f of fields) {
        if (Array.isArray(selection[f])) merged[f].push(...selection[f]);
      }
    }
    for (const f of fields) {
      targetSelection[f] = [...new Set(merged[f])].filter(Boolean);
    }
    syncLegacySelectionFromMultiSelection(targetSelection);
    return fields.reduce((sum, f) => sum + (targetSelection[f]?.length || 0), 0);
  }

function drawPlanInteractionOverlay() {
    if (rotationAngleEditor && !currentMultiSelectionRotateContext()) rotationAngleEditor.finish(true);
    if (S.project.currentView !== 'plan') return;
    drawObstacleInteractionOverlay();
    if (!drawActiveCoreGroupRotateHandle()) drawMultiSelectionRotateHandle();
    if (S.drag?.active && S.drag.type === 'planBoxSelect') {
      drawPlanSelectionBoxOverlay(
        S.drag.startMouse,
        S.drag.currentMouse || S.drag.startMouse,
        S.drag.startScreen,
        S.drag.currentScreen || S.drag.startScreen
      );
    }
    SystemRegistry.drawInteractionOverlays();
  }

function drawPlanSelectionBoxOverlay(start, current, startScreen = null, currentScreen = null) {
    const svg = getPlanSvg();
    if (!svg) return;
    const a = worldToScreen(start.x, start.z);
    const b = worldToScreen(current.x, current.z);
    const crossing = planBoxSelectionMode(startScreen || a, currentScreen || b) === 'crossing';
    svgEl('rect', {
      x: Math.min(a.x, b.x),
      y: Math.min(a.y, b.y),
      width: Math.abs(b.x - a.x),
      height: Math.abs(b.y - a.y),
      fill: crossing ? 'rgba(245, 157, 19, 0.08)' : 'rgba(107, 114, 128, 0.08)',
      stroke: crossing ? '#f59d13' : '#6b7280',
      'stroke-width': 1.5,
      'stroke-dasharray': '6 4',
      'pointer-events': 'none'
    }, svg);
  }

export {
  emptyMultiSelection,
  allMultiSelectionFields,
  syncLegacySelectionFromMultiSelection,
  clearSelectionForActiveSystem,
  hasGlobalSelection,
  clearGlobalSelection,
  clearSelectionBeforeInsert,
  canMoveElement,
  resetTransientInteractionState,
  openLevelsPanelFromNeutralState,
  toggleLevelsTool,
  closeLevelsPanelForToolChange,
  ensureMultiSelection,
  resolvedMultiSelection,
  clearMultiSelection,
  clearForeignMultiSelection,
  clearOwnerMultiSelection,
  countForeignMultiSelection,
  countForeignLegacySelection,
  hasMultiSelection,
  multiSelectionContainsHit,
  multiSelectionEntryFromHit,
  toggleHitInMultiSelection,
  startPlanBoxSelection,
  finishPlanBoxSelection,
  normalizedWorldRect,
  planBoxSelectionMode,
  collectObjectsInWorldRect,
  obstacleBoundsMatchesRect,
  normalizeBoxSelection,
  applyBoxSelection,
  startMultiSelectionDrag,
  buildMultiSelectionMoveSnapshot,
  multiSelectionMoveSnapshotCount,
  applyMultiSelectionMoveDelta,
  applyMultiSelectionRotation,
  drawMultiSelectionRotateHandle,
  finishPlanRotationOnFocusLoss,
  applyMultiSelectionDragPreview,
  clearMultiSelectionDragPreview,
  syncSelectionAfterMultiMove,
  drawPlanInteractionOverlay,
  drawPlanSelectionBoxOverlay
};
